import { DSToggle } from "@components/design-system/molecules";
import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import {
  Controller,
  useFormContext,
  useWatch,
} from "react-hook-form";

import HintCard from "./HintCard";
import { WIZARD_HINT_CONTENT } from "./hintContent";
import { useFieldFocus } from "./useFieldFocus";
import WizardStep from "./WizardStep";
import ChooseTemplateType from "../TemplateBuilder/components/ChooseTemplateType";
import TemplateFields from "../TemplateBuilder/components/TemplateFields";
import ConfirmTemplateTypeModal from "../TemplateBuilder/modals/ConfirmTemplateTypeModal";
import { resetTemplateFields } from "../utils";

export default function TemplateStructureStep({
  control,
  onNext,
  onBack,
  templateUuid,
}) {
  const { setValue } = useFormContext();
  const { releaseWebDentalChartToggle } = useFlags();
  const { focusedField, containerRef } = useFieldFocus();
  const hint = focusedField
    ? WIZARD_HINT_CONTENT[focusedField]
    : null;

  const templateFields = useWatch({
    control,
    name: "templateFields",
  });
  const templateType = useWatch({ control, name: "templateType" });
  const hasContent = templateFields?.some((field) =>
    field.fieldValue?.trim(),
  );

  const [showConfirmTypeChange, setShowConfirmTypeChange] =
    useState(false);
  const [pendingType, setPendingType] = useState(null);

  const handleTemplateTypeChange = (e) => {
    const newType = e.target.value;
    if (hasContent) {
      setPendingType(newType);
      setShowConfirmTypeChange(true);
    } else {
      resetTemplateFields(setValue, newType);
      mixpanel.track("Wizard Template Type Changed", {
        templateUuid,
        newType,
      });
    }
  };

  return (
    <WizardStep
      title="Edit Template Structure"
      description="This helps Scribenote's AI understand what overall structure you'd like for your note. You can include specific preferences in each section here, or set whole-note preferences in the next step."
      onNext={onNext}
      onBack={onBack}
      nextDisabled={!hasContent}
      dataTestId="wizard-step-template-structure"
    >
      <ConfirmTemplateTypeModal
        shouldShow={showConfirmTypeChange}
        hideModal={() => {
          setShowConfirmTypeChange(false);
          setPendingType(null);
        }}
        confirmAction={() => {
          setShowConfirmTypeChange(false);
          if (pendingType) {
            resetTemplateFields(setValue, pendingType);
            mixpanel.track("Wizard Template Type Changed", {
              templateUuid,
              newType: pendingType,
            });
          }
          setPendingType(null);
        }}
      />
      <div className="flex gap-lg" ref={containerRef}>
        <div className="flex-1 min-w-0 flex flex-col gap-md">
          <ChooseTemplateType
            handleTemplateTypeChange={handleTemplateTypeChange}
            formData={{ templateType }}
            belowFreeform={
              templateType === "CUSTOM" &&
              releaseWebDentalChartToggle ? (
                <div className="flex items-center space-x-3">
                  <Controller
                    control={control}
                    name="dentalChart"
                    render={({ field }) => (
                      <DSToggle
                        checked={field.value ?? false}
                        onChange={(e) =>
                          field.onChange(e.target.checked)
                        }
                        dataTestId="dental-chart-toggle"
                        aria-label="Add Dental Chart"
                      />
                    )}
                  />
                  <label className="font-semibold flex items-center gap-1.5">
                    Add Dental Chart 🦷
                    <Tooltip
                      title="Note will generate with a visual dental chart."
                      arrow
                    >
                      <span className="cursor-default text-secondary">
                        <FontAwesomeIcon
                          icon={faCircleQuestion}
                          className="text-xs"
                        />
                      </span>
                    </Tooltip>
                  </label>
                </div>
              ) : undefined
            }
          />
          <TemplateFields
            templateUuid={templateUuid}
            control={control}
          />
        </div>
        <div className="hidden lg:block w-72 shrink-0">
          <div className="sticky top-32">
            <HintCard
              title={hint?.title}
              body={hint?.body}
              visible={!!hint}
              dataTestId="wizard-structure-hint"
            />
          </div>
        </div>
      </div>
    </WizardStep>
  );
}
